import { z } from 'zod';
import { apiGet, apiSend } from '@/lib/api';

/**
 * TOKEN DLA CHMUROWEGO MASTERA — dwie wartości, które umie podać TYLKO portal.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ POWSTAŁO ZE ZGŁOSZENIA Z PIERWSZEGO WDROŻENIA PRODUKCYJNEGO (PESA, 27.08.2026), i to nie
 *  była pomyłka człowieka — była WBUDOWANA W PRZEPŁYW. Wdrożenie wymaga DWÓCH sekretów: kodu
 *  rejestracyjnego dla maszyn klienta i tokenu agenta dla chmurowego sidecara. Portal pokazywał
 *  tylko PIERWSZY, z przyciskiem „Kopiuj"; token agenta nie miał w całym froncie ani jednego
 *  ekranu i wydawało się go wklejając JavaScript w konsolę przeglądarki.
 *
 *  Skutek: do sekretu sidecara trafił kod rejestracyjny (`sve_…`) zamiast tokenu (`sva_…`).
 *  Oba mają 47 znaków i różnią się jedną literą prefiksu, więc pomyłka była niewidoczna —
 *  a objawem był `401` co dwie sekundy, bez śladu w tabeli tokenów (odsiew po kształcie idzie
 *  przed zapytaniem do bazy). Diagnoza poszła w cudzy stack i zabrała dzień.
 *
 *  ⚠️ DLATEGO TA KARTA WYDAJE OBIE WARTOŚCI RAZEM. Sam token nie wystarcza: sidecar potrzebuje
 *  jeszcze `SPS_DB` z nazwą bazy mastera, bo w kontenerze nie ma `SPSConfig.XML`, z którego agent
 *  czyta ją na maszynie klienta. Wydawanie ich osobno to drugie miejsce, w którym można podać
 *  jedną, a zapomnieć drugiej.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ ŚCIEŻKI BEZ `/api` — ten prefiks dokłada `apiGet`/`apiSend` (`API_BASE`).
 */

export const WydanyTokenSchema = z.object({
  token: z.string(),
  id: z.string(),
  note: z.string(),
});
export type WydanyToken = z.infer<typeof WydanyTokenSchema>;

export const TokenAgentaSchema = z.object({
  id: z.string(),
  nodeId: z.string(),
  kind: z.enum(['DATABASE', 'MACHINE']),
  label: z.string(),
  /** Pierwszych 12 znaków — NIE jest sekretem i nie wystarcza do użycia. */
  prefix: z.string(),
  createdAt: z.string(),
  createdByEmail: z.string().nullable(),
  expiresAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  lastUsedAt: z.string().nullable(),
  useCount: z.number(),
});
export type TokenAgenta = z.infer<typeof TokenAgentaSchema>;

const ListaTokenowSchema = z.object({ tokens: z.array(TokenAgentaSchema) });

export async function pobierzTokenyAgenta(groupId: string): Promise<TokenAgenta[]> {
  const { tokens } = await apiGet(`/agent-tokens?groupId=${encodeURIComponent(groupId)}`, ListaTokenowSchema);
  return tokens;
}

export async function wydajTokenAgenta(dane: { groupId: string; nodeId: string; label: string }): Promise<WydanyToken> {
  return apiSend(
    'POST',
    '/agent-tokens',
    { groupId: dane.groupId, nodeId: dane.nodeId, kind: 'DATABASE', label: dane.label, expiresAt: null },
    WydanyTokenSchema,
  );
}

export async function odwolajTokenAgenta(id: string): Promise<void> {
  await apiSend('POST', `/agent-tokens/${id}/revoke`);
}

/**
 * NAZWA WĘZŁA CHMUROWEGO MASTERA, wyliczona z kodu wyspy.
 *
 * ⚠️ TO TOŻSAMOŚĆ WĘZŁA W PORTALU I ZOSTAJE NA STAŁE, więc nie może być losowa ani „node-1".
 * `chmura-mssql-<kod wyspy>` czyta się po miesiącach i od razu mówi, że to nie maszyna klienta.
 *
 * ⚠️ Ten sam wzorzec czyszczenia co `nodeIdFromHostname` po stronie API — inaczej kod wyspy
 * z myślnikiem niedozwolonym w nazwie węzła dałby odmowę dopiero przy wydaniu.
 */
export function domyslnyNodeId(kodWyspy: string): string {
  const czysty = kodWyspy
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `chmura-mssql-${czysty === '' ? 'master' : czysty}`.slice(0, 64);
}

/** Nazwa bazy, którą wolno wstawić do bloku ENV — bez odstępów i bez końca wiersza. */
const NAZWA_BAZY = /^[A-Za-z_][A-Za-z0-9_$-]{0,127}$/;

/**
 * BLOK GOTOWY DO WKLEJENIA W SEKRET INSTANCJI — dokładnie te dwie zmienne, których platforma
 * nie umie sobie wyliczyć.
 *
 * ⚠️ DWIE LINIE, NIE WIĘCEJ. Resztę kontraktu (`AGENT_ROLE`, `AGENT_PORTAL_URL`, `MSSQL_*`)
 * platforma ustawia sama i jest ona identyczna dla każdej instancji. Dorzucenie jej tutaj
 * zamieniłoby jedyną wartość, na którą trzeba uważać, w jeden z ośmiu wierszy do przejrzenia.
 *
 * ⚠️ ODMAWIAMY, GDY NAZWY BAZY NIE ZNAMY albo nie nadaje się do bloku ENV. Blok z pustym
 * `SPS_DB=` jest gorszy od braku bloku: wygląda na kompletny, a sidecar wstałby z bazą, której
 * nie ma. Wyspa bez mastera to normalny stan przed rejestracją pierwszego agenta — wtedy ekran
 * mówi to zdaniem, zamiast wydawać połowę.
 */
export function blokDoWklejenia(token: string, bazaMastera: string | null): string | null {
  if (bazaMastera === null) return null;
  const baza = bazaMastera.trim();
  if (!NAZWA_BAZY.test(baza)) return null;
  return `AGENT_PORTAL_TOKEN=${token}\nSPS_DB=${baza}`;
}

/**
 * Czy ta wartość wygląda na token agenta — do sprawdzenia PO wydaniu, na ekranie.
 *
 * ⚠️ SPRAWDZAMY WŁASNY WYNIK, i to nie jest paranoja. Cała ta karta powstała z pomyłki
 * „nie ta rodzina sekretu", więc ekran, który wydaje token, ma pokazać człowiekowi dowód, że
 * wydał TEN rodzaj: prefiks `sva_` i 47 znaków (4 + 43 base64url z 32 bajtów).
 */
export function wygladaNaTokenAgenta(v: string): boolean {
  return /^sva_[A-Za-z0-9_-]{43}$/.test(v);
}

/**
 * NAZWA WĘZŁA, O KTÓRYM MÓWI KARTA CHMUROWEGO MASTERA.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ POWSTAŁO Z BŁĘDU (28.08.2026): karta szukała tokenu po nazwie WYLICZONEJ z kodu wyspy
 *  i na testówce napisała „ten węzeł nie ma jeszcze tokenu", choć token był wydany — węzeł
 *  nazywa się tam `chmura-mssql-dbw`, a z kodu wyspy `vendi-test-dbw` wychodzi
 *  `chmura-mssql-vendi-test-dbw`. Na PESA obie nazwy przypadkiem się zgadzały, więc błąd nie
 *  miał jak wyjść — i dlatego ta funkcja jest osobna i ma test.
 *
 *  ⚠️ ROZPOZNAJEMY PO BRAKU AUTOMATU, nie po nazwie. Ta sama definicja, którą rozstrzyga API
 *  (`czyToZasianieMastera`): master wyspy nie ma własnego automatu, bo jest wyłącznie bazą.
 *  Nazwa to dowolny napis od człowieka i nie wolno na niej opierać tożsamości.
 *
 *  ⚠️ WYLICZONA NAZWA ZOSTAJE JAKO PODPOWIEDŹ DLA PIERWSZEGO WYDANIA — wtedy węzła jeszcze nie
 *  ma, więc nie ma czego znaleźć, a token wystawia się WŁAŚNIE po to, żeby mógł się zgłosić.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function nazwaWezlaMastera(
  agenci: readonly {
    group: { id: string };
    kind: string;
    machine: unknown;
    /** `null` = węzeł nie zgłosił VMID-u, czyli nie widział `SPSConfig.XML` — patrz niżej. */
    spsVmid?: number | null;
    nodeId: string;
  }[],
  groupId: string,
  kodWyspy: string,
): string {
  const wezel = agenci.find(
    (a) =>
      a.group.id === groupId &&
      a.kind === 'DATABASE' &&
      (a.machine === null || a.machine === undefined) &&
      /**
       * ⚠️⚠️ SAM BRAK AUTOMATU TO ZA MAŁO, i to jest poprawka zgłoszenia z 01.09.2026: na instancji
       * postawionej od zera pierwszy agent maszyny (HOST_BAZY, VMID 3) nie miał jeszcze dowiązanego
       * automatu — bo automat powstaje dopiero z pierwszej synchronizacji konfiguracji — więc ta
       * karta wzięła GO za chmurowego mastera i pokazała jego token jako „token tego węzła".
       * Kliknięcie „Wydaj token" odwołałoby wtedy token, którym pracujący agent właśnie pisze.
       *
       * ⚠️ VMID jest tym, czego sidecar nie ma i mieć nie będzie (ustalone 25.08.2026: „VMID nie
       * będzie miał, bo nie ma swojego automatu, jest tylko bazą") — w kontenerze nie ma
       * `SPSConfig.XML`, z którego VMID się bierze.
       */
      (a.spsVmid === null || a.spsVmid === undefined),
  );
  return wezel?.nodeId ?? domyslnyNodeId(kodWyspy);
}
